/**
 * Dependency-free SVG charts for dashboard widgets and artifact KPIs.
 * Line (timeseries) and bar (breakdown) variants; theme-safe currentColor.
 */

import React from 'react';
import type { MetricRow } from '../../types/marketingSuite';

const WIDTH = 560;
const HEIGHT = 160;
const PAD = { top: 12, right: 12, bottom: 24, left: 48 };

function niceValue(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  if (Math.abs(v) >= 100) return v.toFixed(0);
  return `${Math.round(v * 100) / 100}`;
}

const SERIES_COLORS = [
  '#5c6ac4',
  '#47c1bf',
  '#f49342',
  '#9c6ade',
  '#50b83c',
  '#de3618',
];

export function LineChart({
  rows,
  unit,
}: {
  rows: MetricRow[];
  unit: string;
}) {
  // Native amounts in different currencies are NOT comparable: one
  // shared numeric scale lets a JPY series flatten a USD series and the
  // plotted geometry implies a comparison that doesn't exist (PR #11
  // review). Monetary multi-ledger results facet into one chart per
  // currency, each with its own scale.
  const usableAll = rows.filter((r) => typeof r.value === 'number');
  if (unit === 'currency') {
    const ledgers = [
      ...new Set(usableAll.map((r) => r.currency).filter(Boolean)),
    ] as string[];
    if (ledgers.length > 1 && ledgers.length === new Set(usableAll.map((r) => r.currency)).size) {
      return (
        <div>
          {ledgers.map((ledger) => (
            <div key={ledger}>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--p-color-text-secondary)',
                  marginTop: 4,
                }}
              >
                {ledger}
              </div>
              <SingleScaleLineChart
                rows={usableAll.filter((r) => r.currency === ledger)}
                unit={unit}
              />
            </div>
          ))}
        </div>
      );
    }
  }
  return <SingleScaleLineChart rows={usableAll} unit={unit} />;
}

function SingleScaleLineChart({
  rows,
  unit,
}: {
  rows: MetricRow[];
  unit: string;
}) {
  // Grouped results interleave rows for the same period — monetary metrics
  // carry an implicit per-currency grouping, breakdowns a platform one.
  // Connecting them into a single path would zig-zag between series and
  // read as a trend that doesn't exist, so each (platform, currency)
  // combination becomes its own line.
  const usable = rows.filter((r) => typeof r.value === 'number');
  const periods: string[] = [];
  const seriesMap = new Map<
    string,
    { period: string; value: number; currency?: string }[]
  >();
  for (const r of usable) {
    const period = r.period_end || '';
    if (!periods.includes(period)) periods.push(period);
    const key = [r.platform, r.currency].filter(Boolean).join(' · ') || 'all';
    const list = seriesMap.get(key) ?? [];
    list.push({ period, value: r.value as number, currency: r.currency });
    seriesMap.set(key, list);
  }
  if (periods.length < 2) {
    return (
      <div style={{ color: 'var(--p-color-text-secondary)' }}>
        Not enough history to chart yet.
      </div>
    );
  }
  const values = usable.map((r) => r.value as number);
  const min = Math.min(...values, 0);
  const max = Math.max(...values);
  const span = max - min || 1;
  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  const x = (period: string) =>
    PAD.left + (periods.indexOf(period) / (periods.length - 1)) * innerW;
  const y = (v: number) => PAD.top + innerH - ((v - min) / span) * innerH;
  const series = [...seriesMap.entries()].map(([key, pts], idx) => ({
    key,
    pts,
    color: SERIES_COLORS[idx % SERIES_COLORS.length],
  }));
  // Labels go through the unit-aware formatter: a percent series whose
  // semantic value is 0.2 must read 20% (PR #11 review). Per-series
  // "latest" values carry that series' ledger; the shared axis spans
  // series that may be in DIFFERENT currencies, so axis bounds stay
  // neutral amounts rather than claiming one ledger.
  const axisValue = (v: number) => formatMetricValue(v, unit);
  const aria = `Trend from ${periods[0]} to ${periods[periods.length - 1]}: ${series
    .map((s) => {
      const last = s.pts[s.pts.length - 1];
      return `${s.key} latest ${formatMetricValue(last.value, unit, last.currency)}`;
    })
    .join(', ')}, range ${axisValue(min)} to ${axisValue(max)}.`;
  return (
    <div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        role="img"
        aria-label={aria}
      >
        <line
          x1={PAD.left}
          y1={y(min)}
          x2={WIDTH - PAD.right}
          y2={y(min)}
          stroke="currentColor"
          strokeOpacity={0.2}
        />
        {series.map((s) => (
          <g key={s.key}>
            <path
              d={s.pts
                .map(
                  (p, i) =>
                    `${i === 0 ? 'M' : 'L'}${x(p.period).toFixed(1)},${y(
                      p.value
                    ).toFixed(1)}`
                )
                .join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={2.5}
            />
            {s.pts.map((p, i) => (
              <circle
                key={i}
                cx={x(p.period)}
                cy={y(p.value)}
                r={2.4}
                fill={s.color}
              />
            ))}
          </g>
        ))}
        <text x={PAD.left} y={HEIGHT - 6} fontSize={11} fill="currentColor" opacity={0.7}>
          {periods[0]}
        </text>
        <text
          x={WIDTH - PAD.right}
          y={HEIGHT - 6}
          fontSize={11}
          textAnchor="end"
          fill="currentColor"
          opacity={0.7}
        >
          {periods[periods.length - 1]}
        </text>
        <text x={4} y={y(max) + 4} fontSize={11} fill="currentColor" opacity={0.7}>
          {axisValue(max)}
        </text>
        <text x={4} y={y(min)} fontSize={11} fill="currentColor" opacity={0.7}>
          {axisValue(min)}
        </text>
      </svg>
      {series.length > 1 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px 12px',
            marginTop: 4,
            fontSize: 12,
            color: 'var(--p-color-text-secondary)',
          }}
        >
          {series.map((s) => (
            <span
              key={s.key}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <span
                style={{
                  width: 10,
                  height: 3,
                  borderRadius: 2,
                  background: s.color,
                  display: 'inline-block',
                }}
              />
              {s.key}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function formatMetricValue(
  value: number,
  unit: string,
  currency?: unknown
): string {
  if (unit === 'currency') {
    // Currency codes are warehouse DATA: Intl.NumberFormat throws a
    // RangeError on anything that isn't a well-formed 3-letter code, and
    // on the public share page that unmounts the whole view (PR #11
    // review). A missing/invalid code renders the plain number rather
    // than mislabeling a non-USD tenant as USD.
    if (typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency)) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency.toUpperCase(),
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value);
    }
    return niceValue(value);
  }
  if (unit === 'percent') {
    // Semantic percent metrics are FRACTIONS by contract — inferring the
    // representation from magnitude misrendered legitimate rates above
    // 100% (e.g. engagement 1.5 → "1.50%") (PR #11 review).
    return `${(value * 100).toFixed(2)}%`;
  }
  if (unit === 'ratio') return `${value.toFixed(2)}x`;
  return niceValue(value);
}

export function BarChart({
  rows,
  unit,
}: {
  rows: MetricRow[];
  unit: string;
}) {
  const latestPeriod = rows.length ? rows[rows.length - 1].period_end : undefined;
  const latest = rows.filter(
    (r) => r.period_end === latestPeriod && typeof r.value === 'number'
  );
  // Native amounts in different currencies are NOT comparable: ranking
  // every platform/currency row on one magnitude scale let a JPY ledger
  // evict every USD bar from the top eight and flatten the survivors
  // (PR #11 review). Monetary multi-ledger breakdowns facet into one
  // bar section per currency, each with its own scale — exactly like
  // LineChart.
  if (unit === 'currency') {
    const ledgers = [
      ...new Set(latest.map((r) => r.currency).filter(Boolean)),
    ] as string[];
    if (
      ledgers.length > 1 &&
      ledgers.length === new Set(latest.map((r) => r.currency)).size
    ) {
      return (
        <div>
          {ledgers.map((ledger) => (
            <div key={ledger}>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--p-color-text-secondary)',
                  marginTop: 4,
                }}
              >
                {ledger}
              </div>
              <SingleScaleBars
                rows={latest.filter((r) => r.currency === ledger)}
                unit={unit}
              />
            </div>
          ))}
        </div>
      );
    }
  }
  return <SingleScaleBars rows={latest} unit={unit} />;
}

function SingleScaleBars({
  rows,
  unit,
}: {
  // Latest-period rows only — and a single ledger when monetary.
  rows: MetricRow[];
  unit: string;
}) {
  const bars = rows
    .map((r) => ({
      // Keep BOTH dimensions when both exist: a platform breakdown on a
      // monetary metric returns one row per platform/currency pair, and
      // collapsing them rendered indistinguishable duplicate bars
      // (PR #11 review). For monetary metrics the ledger code rides the
      // VALUE ("USD 100"), so it is dropped from the label rather than
      // printed twice.
      label:
        [r.platform, unit === 'currency' ? undefined : r.currency]
          .filter(Boolean)
          .join(' · ') || 'all',
      value: r.value as number,
      currency: r.currency,
    }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .slice(0, 8);
  if (!bars.length) {
    return (
      <div style={{ color: 'var(--p-color-text-secondary)' }}>No data yet.</div>
    );
  }
  // Value labels go through the unit-aware formatter: a CTR breakdown
  // whose semantic value is 0.2 must read 20%, not 0.2 (PR #11 review).
  const fmt = (b: { value: number; currency?: string }) =>
    formatMetricValue(b.value, unit, b.currency);
  // Scale and rank by magnitude so negative bars keep proportional
  // widths (an all-negative net-revenue series used to collapse to 2px
  // stubs); negatives get the critical color instead of a direction.
  const max = Math.max(...bars.map((b) => Math.abs(b.value))) || 1;
  const rowH = 26;
  const height = bars.length * rowH + 8;
  const labelW = 130;
  const aria = `Breakdown: ${bars
    .map((b) => `${b.label} ${fmt(b)}`)
    .join(', ')}.`;
  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${height}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      role="img"
      aria-label={aria}
    >
      {bars.map((b, i) => {
        const w = ((WIDTH - labelW - 70) * Math.abs(b.value)) / max;
        const yPos = i * rowH + 4;
        return (
          <g key={`${b.label}-${i}`}>
            <text
              x={labelW - 8}
              y={yPos + 14}
              fontSize={12}
              textAnchor="end"
              fill="currentColor"
            >
              {b.label}
            </text>
            <rect
              x={labelW}
              y={yPos}
              width={Math.max(w, 2)}
              height={rowH - 8}
              rx={3}
              fill={b.value < 0 ? '#de3618' : '#5c6ac4'}
            />
            <text
              x={labelW + Math.max(w, 2) + 6}
              y={yPos + 14}
              fontSize={12}
              fill="currentColor"
            >
              {fmt(b)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function SingleKpi({
  row,
  unit,
  directionIsImprovement,
  compact,
}: {
  row: MetricRow;
  unit: string;
  directionIsImprovement: string;
  compact: boolean;
}) {
  const value = row.value ?? 0;
  const prior = row.prior_value;
  const formatted = formatMetricValue(value, unit, row.currency);
  let deltaEl: React.ReactNode = null;
  if (typeof prior === 'number' && prior !== 0) {
    // Divide by the MAGNITUDE: a raw negative prior flips the sign, so a
    // net metric improving from -100 to -50 rendered as a critical
    // decline (PR #11 review).
    const pct = ((value - prior) / Math.abs(prior)) * 100;
    // Spend/CAC/CPA improve DOWNWARD: an increase is the critical color.
    const isGood = directionIsImprovement === 'down' ? pct <= 0 : pct >= 0;
    deltaEl = (
      <span
        style={{
          fontSize: 13,
          color: isGood
            ? 'var(--p-color-text-success)'
            : 'var(--p-color-text-critical)',
        }}
      >
        {pct >= 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
      </span>
    );
  }
  // Non-currency units don't reveal which ledger they belong to, so the
  // currency tag rides along in the multi-currency layout.
  const tag =
    compact && row.currency && unit !== 'currency' ? (
      <span
        style={{ fontSize: 12, color: 'var(--p-color-text-secondary)' }}
      >
        {String(row.currency)}
      </span>
    ) : null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
      <span style={{ fontSize: compact ? 18 : 26, fontWeight: 650 }}>
        {formatted}
      </span>
      {tag}
      {deltaEl}
    </span>
  );
}

export function KpiValue({
  rows,
  unit,
  directionIsImprovement = 'up',
}: {
  rows: MetricRow[];
  unit: string;
  directionIsImprovement?: string;
}) {
  if (!rows.length) return <span>—</span>;
  // Grouped monetary metrics return one latest-period row PER CURRENCY —
  // rows[-1] alone displayed an arbitrary currency that could change
  // between executions (PR #11 review). Every latest-period row renders.
  const latestPeriod = rows[rows.length - 1].period_end;
  const latestRows = rows.filter(
    (r) => r.period_end === latestPeriod && typeof r.value === 'number'
  );
  if (!latestRows.length) return <span>—</span>;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {latestRows.map((row, i) => (
        <SingleKpi
          key={i}
          row={row}
          unit={unit}
          directionIsImprovement={directionIsImprovement}
          compact={latestRows.length > 1}
        />
      ))}
    </span>
  );
}
